import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	Line,
	LineChart,
	Area,
	AreaChart,
	LabelList,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';
import { analyticsAPI } from '../../../services/constants';
import { request } from '../../../services/utilities';
import { notifyError } from '../../../services/notify';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';

const StaffStoreOverview = ({ selectedStaff }) => {
	const [loading, setLoading] = useState({
		trend: true,
		summary: true,
		moved: true,
		requisitionTrend: true,
		stats: true,
		value: true,
	});
	const [filters, setFilters] = useState({
		summary: { dateFilter: 'past-month' },
		moved: { dateFilter: 'past-month' },
		trend: { dateFilter: 'past-month' },
		stats: { dateFilter: 'past-month' },
		value: { dateFilter: 'past-month' },
	});
	const prevFiltersRef = useRef(null);
	const [summary, setSummary] = useState({});
	const [topMoved, setTopMoved] = useState([]);
	const [valueTrend, setValueTrend] = useState([]);
	const [requisitionTrend, setRequisitionTrend] = useState([]);
	const [stats, setStats] = useState({});

	const COLORS = [
		'#00C49F', // green
		'#FF4444', // red
		'#FFBB28', // yellow
		'#0088FE', // blue
		'#FF8042', // orange
		'#8884d8', // purple
		'#FF6699', // pink
		'#A28EFF', // light purple
		'#33CCFF', // sky blue
		'#22BB33', // dark green
	];

	const onFilterChange = (filterKey, value) => {
		setFilters(prev => ({
			...prev,
			[filterKey]: {
				...(prev[filterKey] || {}),
				dateFilter: value,
			},
		}));
	};

	const TimeRangeDropdown = ({ chartId, value }) => {
		return (
			<div className="mb-4">
				<form className="form-inline justify-content-sm-end">
					<select
						className="form-control form-control-sm rounded"
						onChange={e => {
							onFilterChange(chartId, e.target.value);
						}}
						value={value}
					>
						<option value="today">Today</option>
						<option value="past-week">Past Week</option>
						<option value="past-month">Past 30 Days</option>
						<option value="last-month">Last Month</option>
						<option value="past-quarter">Past Quarter</option>
						<option value="past-six-months">Past Six Months</option>
						<option value="past-year">Past Year</option>
						<option value="last-year">Last Year</option>
						<option value="past-three-years">Past Three Years</option>
						<option value="all-time">All Time</option>
					</select>
				</form>
			</div>
		);
	};

	// fetch requisition trend based on current filter;
	const fetchRequisitionTrend = useCallback(async () => {
		const url = `${analyticsAPI}/requisition-trend?dateFilter=${filters?.trend?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.trend?.dateFilter]);

	// fetch Top Moved based on current filter
	const fetchTopMovedItems = useCallback(async () => {
		const url = `${analyticsAPI}/top-moved-items?dateFilter=${filters?.moved?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.moved?.dateFilter]);

	//fetch value trend based on current filter
	const fetchValueTrend = useCallback(async () => {
		const url = `${analyticsAPI}/inventory-value-trend?dateFilter=${filters?.value?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.value?.dateFilter]);

	//fetch inventory summary based on current filter
	const fetchSummary = useCallback(async () => {
		const url = `${analyticsAPI}/inventory-summary?dateFilter=${filters?.summary?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.summary?.dateFilter]);

	//fetch requisition stats based on current filter
	const fetchRequisitionStats = useCallback(async () => {
		const url = `${analyticsAPI}/requisition-stats?dateFilter=${filters?.stats?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.stats?.dateFilter]);

	//
	useEffect(() => {
		const prevFilters = prevFiltersRef.current;

		const fetchMap = {
			trend: fetchRequisitionTrend,
			moved: fetchTopMovedItems,
			summary: fetchSummary,
			stats: fetchRequisitionStats,
			value: fetchValueTrend,
		};

		Object.entries(fetchMap).forEach(([key, fetchFn]) => {
			const currentFilter = filters?.[key]?.dateFilter;
			const prevFilter = prevFilters?.[key]?.dateFilter;

			if (currentFilter && (!prevFilter || currentFilter !== prevFilter)) {
				setLoading(prev => ({ ...prev, [key]: true }));

				fetchFn()
					.then(data => {
						if (key === 'trend') {
							setRequisitionTrend(data);
						} else if (key === 'moved') {
							setTopMoved(data);
						} else if (key === 'stats') {
							setStats(data);
						} else if (key === 'value') {
							setValueTrend(data);
						} else if (key === 'summary') {
							setSummary(data);
						}
					})
					.catch(error => notifyError('Error fetching request count'))
					.finally(() => setLoading(prev => ({ ...prev, [key]: false })));
			}
		});

		// Update ref after processing
		prevFiltersRef.current = filters;
	}, [filters]);

	//All chart data(s)

	// chart data for age range
	const TopMovedChart = topMoved
		?.sort(() => Math.random() - 0.5)
		?.map(item => ({
			name: item?.name || 'Unknown',
			count: parseInt(item?.totalquantity, 10),
		}));

	//requisition trend chart
	const RequisitionTrendChart = requisitionTrend
		// ?.sort((a, b) => b.total - a.total)
		?.sort(() => Math.random() - 0.5)
		?.map(item => ({
			name: item?.department || 'Unknown',
			count: parseInt(item?.total, 10),
		}))
		.slice(0, 30);

	//requisition-stats chart
	const RequisitionStatsChart = stats
		? Object.entries(stats)
				.filter(([key]) => key !== 'total')
				.map(([name, value]) => ({
					name: name.charAt(0).toUpperCase() + name.slice(1),
					value: parseInt(value, 10),
				}))
		: [];

	const legendData = [...RequisitionStatsChart, { name: 'Total', value: parseInt(stats?.total || 0, 10) }];

	//inventory summary chart
	const InventorySummaryChart = summary
		? Object.entries(summary).map(([name, value]) => ({
				name: name.charAt(0).toUpperCase() + name.slice(1),
				value: parseInt(value, 10),
		  }))
		: [];

	//value trend
	const ValueTrendChart = valueTrend
		?.sort((a, b) => b.total_value - a.total_value)
		?.sort(() => Math.random() - 0.5)
		?.map(item => ({
			name: item?.date || item?.year || item?.month || 'Unknown',
			count: parseInt(item?.total_value, 10),
		}))
		.slice(0, 10);

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="row">
					{/* Requisition Stats Trends*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Requisition Trends </h6>

							<TimeRangeDropdown chartId="trend" value={filters?.trend?.dateFilter} />
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Showcases requisition types by count, providing insights into the most frequently requested items or services and their
								usage trends based on selected date filters.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.trend ? (
												<TableLoading />
											) : RequisitionTrendChart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<LineChart data={RequisitionTrendChart} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey={'name'}
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>

															<Legend wrapperStyle={{ marginBottom: '-50px' }} />
															<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																<LabelList
																	dataKey="count"
																	position="top"
																	content={renderCustomLabelAlt}
																	style={{ fontSize: '10px', fill: '#333' }}
																/>
															</Line>
														</LineChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Top Requisition Stats */}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header"> Requisition Stats Distribution </h6>

							<TimeRangeDropdown chartId="stats" value={filters?.stats?.dateFilter} />
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Summarizes requisition requests over time, offering visibility into frequency, approval rates, and departmental demand
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.stats ? (
												<TableLoading />
											) : RequisitionStatsChart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<PieChart width={400} height={400}>
															<Pie
																data={RequisitionStatsChart}
																dataKey="value"
																nameKey="name"
																cx="50%"
																cy="50%"
																outerRadius={150}
																fill="#8884d8"
																label={({ name, value }) => `${Number(value).toLocaleString()}`}
															>
																{RequisitionStatsChart.map((entry, index) => (
																	<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																))}
															</Pie>
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Legend
																content={() => (
																	<ul
																		style={{
																			display: 'flex',
																			flexWrap: 'wrap',
																			gap: '1rem',
																			listStyle: 'none',
																			padding: 0,
																			margin: 0,
																			alignItems: 'center',
																		}}
																	>
																		{legendData.map((item, index) => (
																			<li
																				key={index}
																				style={{
																					display: 'flex',
																					alignItems: 'center',
																					gap: '0.5rem',
																				}}
																			>
																				<span
																					style={{
																						display: 'inline-block',
																						width: '12px',
																						height: '12px',
																						backgroundColor: index < COLORS.length ? COLORS[index % COLORS.length] : '#999',
																						borderRadius: '2px',
																					}}
																				/>
																				<span>
																					{item.name}: {item.value.toLocaleString()}
																				</span>
																			</li>
																		))}
																	</ul>
																)}
															/>
														</PieChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Top Moved Items */}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header"> Top Moved Items </h6>

							<TimeRangeDropdown chartId="moved" value={filters?.moved?.dateFilter} />

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Showcases the most frequently moved items or resources by count, providing insights into the most active inventory based on
								selected date filters.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.moved ? (
												<TableLoading />
											) : TopMovedChart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<AreaChart data={TopMovedChart} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey={'name'}
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Area
																type="monotone"
																dataKey="count"
																stroke="#10B981"
																fill="#10B981"
																strokeWidth={2}
																dot={{ r: 4 }}
																activeDot={{ r: 6 }}
																name="count"
															>
																<LabelList
																	dataKey="count"
																	content={renderCustomLabelAlt}
																	position="top"
																	style={{ fontSize: '12px', fill: '#333' }}
																/>
															</Area>
														</AreaChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Inventory value Trends*/}
					<div className="col-sm-12 col-lg-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Inventory Value Trend </h6>

							<TimeRangeDropdown chartId="value" value={filters?.value?.dateFilter} />

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Illustrates changes in total inventory value over time, helping track stock valuation patterns and budget impact
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.value ? (
												<TableLoading />
											) : ValueTrendChart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<LineChart data={ValueTrendChart} margin={{ top: 20, right: 30, left: 80, bottom: 50 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey={'name'}
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Legend wrapperStyle={{ marginBottom: '-50px' }} />
															<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																<LabelList
																	dataKey="count"
																	content={renderCustomLabelAlt}
																	position="top"
																	style={{ fontSize: '10px', fill: '#333' }}
																/>
															</Line>
														</LineChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Inventory Summary*/}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header"> Inventory Summary</h6>

							<TimeRangeDropdown chartId="summary" value={filters?.summary?.dateFilter} />

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Provides a breakdown of inventory by stock status or category, offering a snapshot of overall stock distribution and
								availability.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.summary ? (
												<TableLoading />
											) : InventorySummaryChart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<PieChart width={400} height={400}>
															<Pie
																data={InventorySummaryChart}
																dataKey="value"
																nameKey="name"
																cx="50%"
																cy="50%"
																outerRadius={150}
																fill="#8884d8"
																label
															>
																{InventorySummaryChart.map((entry, index) => (
																	<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																))}
															</Pie>
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Legend wrapperStyle={{ marginBottom: '-10px' }} />
														</PieChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
export default StaffStoreOverview;
